import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    TablePagination,
    styled,
    tableCellClasses,
    Switch
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { green, red } from "@mui/material/colors";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import { urlService } from "../../utils/url";
import axios from "axios";

const StyledTableHead = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#00a62b",
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const GreenSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: green[600],
        "&:hover": {
            backgroundColor: alpha(green[600], theme.palette.action.hoverOpacity),
        },
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: green[600],
    },
}));


const TablaBlog = ({ filtroTexto, filtroCategoria, filtroEstado }) => {
    const history = useHistory();
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${urlService}/api/blog/admin`);
                setData(response.data);
            } catch (error) {
                console.error("Error al obtener los blogs", error);
            }
        };
        fetchData();
    }, []);

    // Apply filters
    const filteredData = data.filter((item) => {
        const authorToSearch = item.esExterno ? (item.firmaApellido || "") : (item.autor || "");
        const matchTexto = (item.titulo && item.titulo.toLowerCase().includes(filtroTexto.toLowerCase())) ||
            (authorToSearch.toLowerCase().includes(filtroTexto.toLowerCase()));
        const matchCategoria = filtroCategoria === "" || item.categoria === filtroCategoria;
        const matchEstado = filtroEstado === "" ||
            (filtroEstado === "publicado" ? item.publicado : !item.publicado);

        return matchTexto && matchCategoria && matchEstado;
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEdit = (id) => {
        history.push(`/blog/editar/${id}`);
    };

    const handleView = (row) => {
        const urlWeb = window.location.hostname.includes("dev.propins.cl") ||
            window.location.hostname.includes("localhost")
            ? "https://dev.propins.cl"
            : "https://propins.cl";
        const previewUrl = `${urlWeb}/blog/preview/${row.previewHash}`;
        window.open(previewUrl, "_blank");
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: `¿Eliminar artículo?`,
            text: `Confirma que desea eliminar este artículo`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Confirmar",
        }).then(async (result) => {
            if (result.value) {
                try {
                    await axios.delete(`${urlService}/api/blog/${id}`);
                    setData(data.filter(item => item.id !== id));
                    Swal.fire("Eliminado!", "El artículo ha sido eliminado.", "success");
                } catch (error) {
                    Swal.fire("Error!", "Hubo un problema al eliminar el artículo.", "error");
                }
            }
        });
    };

    const handleTogglePublish = (id, currentStatus) => {
        const action = currentStatus ? "despublicar" : "publicar";
        Swal.fire({
            title: `¿${action.charAt(0).toUpperCase() + action.slice(1)} artículo?`,
            text: `Confirma que desea ${action} este artículo`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Confirmar",
        }).then(async (result) => {
            if (result.value) {
                try {
                    await axios.put(`${urlService}/api/blog/${id}/publish`);
                    setData(data.map(item => item.id === id ? { ...item, publicado: !currentStatus } : item));
                    Swal.fire("Éxito!", `El artículo ha sido ${currentStatus ? 'despublicado' : 'publicado'}.`, "success");
                } catch (error) {
                    Swal.fire("Error!", "Hubo un problema al cambiar el estado del artículo.", "error");
                }
            }
        });
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden', mt: 3 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            <StyledTableHead align="center">Título</StyledTableHead>
                            <StyledTableHead align="center">Categoría</StyledTableHead>
                            <StyledTableHead align="center">Tipo</StyledTableHead>
                            <StyledTableHead align="center">Autor</StyledTableHead>
                            <StyledTableHead align="center">Acciones</StyledTableHead>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                        <TableCell align="left">{row.titulo}</TableCell>
                                        <TableCell align="center" style={{ textTransform: 'capitalize' }}>{row.categoria}</TableCell>
                                        <TableCell align="center">{row.esExterno ? "Externo" : "Interno"}</TableCell>
                                        <TableCell align="center">{row.esExterno ? row.firmaApellido : row.autor}</TableCell>
                                        <TableCell align="center" style={{ minWidth: 150 }}>
                                            <Tooltip title="Ver">
                                                <IconButton onClick={() => handleView(row)}>
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={row.publicado ? "Despublicar" : "Publicar"}>
                                                <GreenSwitch
                                                    checked={row.publicado}
                                                    onChange={() => handleTogglePublish(row.id, row.publicado)}
                                                />
                                            </Tooltip>
                                            <Tooltip title="Eliminar">
                                                <IconButton onClick={() => handleDelete(row.id)}>
                                                    <DeleteIcon sx={{ color: red[500] }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton onClick={() => handleEdit(row.id)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={""}
            />
        </Paper>
    );
};

export default TablaBlog;
